import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { Trash2, Eye, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/handball";

/**
 * ProfileShareDialog
 *   Owner-only: manage who has access to a goalkeeper profile and their role.
 */
export default function ProfileShareDialog({
  open,
  onOpenChange,
  goalkeeperId,
  goalkeeperName,
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!goalkeeperId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/goalkeepers/${goalkeeperId}/shares`);
      setShares(data);
    } catch {
      setShares([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setEmail("");
      setRole("viewer");
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, goalkeeperId]);

  const submit = async () => {
    if (!email.trim()) {
      toast.error("Ange en e-postadress");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/goalkeepers/${goalkeeperId}/shares`, {
        email: email.trim(),
        role,
      });
      toast.success("Åtkomst beviljad");
      setEmail("");
      setRole("viewer");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte dela profilen");
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (shareId, nextRole) => {
    try {
      await api.patch(`/profile-shares/${shareId}`, { role: nextRole });
      toast.success("Behörighet uppdaterad");
      await load();
    } catch {
      toast.error("Kunde inte ändra behörighet");
    }
  };

  const removeShare = async (shareId) => {
    try {
      await api.delete(`/profile-shares/${shareId}`);
      toast.success("Åtkomst borttagen");
      await load();
    } catch {
      toast.error("Kunde inte ta bort delningen");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#111] border-[#27272A] text-white max-h-[90vh] overflow-y-auto"
        data-testid="profile-share-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-widest text-xl">
            Dela profil
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {goalkeeperName} – ge en annan användare åtkomst med valt
            behörighetssteg.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-zinc-400 uppercase tracking-widest text-xs">
              E-postadress
            </Label>
            <Input
              data-testid="profile-share-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="person@exempel.se"
              className="bg-black border-[#3F3F46] text-white focus-visible:ring-[#34C759]"
            />
            <div className="text-[11px] text-zinc-500">
              Mottagaren måste redan ha ett godkänt konto.
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-400 uppercase tracking-widest text-xs">
              Behörighet
            </Label>
            <RadioGroup value={role} onValueChange={setRole} className="space-y-2">
              <RoleRow
                value="viewer"
                selected={role === "viewer"}
                testid="profile-share-role-viewer"
                icon={<Eye size={16} className="text-[#34C759]" />}
                title="Läsare"
                subtitle="Ser statistik + kan skriva dagboksanteckningar"
              />
              <RoleRow
                value="editor"
                selected={role === "editor"}
                testid="profile-share-role-editor"
                icon={<Pencil size={16} className="text-[#FFD60A]" />}
                title="Skribent"
                subtitle="Allt + starta sessioner, logga skott, redigera matcher"
              />
            </RadioGroup>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-col sm:space-x-0 gap-2 items-stretch">
          <Button
            data-testid="profile-share-save-btn"
            onClick={submit}
            disabled={saving || !email.trim()}
            className="bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest w-full"
          >
            Dela
          </Button>
        </DialogFooter>

        {shares.length > 0 && (
          <div className="pt-5 mt-2 border-t border-[#27272A] space-y-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-1">
              Nuvarande delningar
            </div>
            {shares.map((s) => (
              <div
                key={s.id}
                data-testid={`profile-share-row-${s.id}`}
                className="flex items-center gap-2 border border-[#27272A] p-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">
                    {s.collaborator_email}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5 uppercase tracking-widest font-display">
                    {s.role === "editor" ? "Skribent" : "Läsare"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    changeRole(s.id, s.role === "editor" ? "viewer" : "editor")
                  }
                  data-testid={`profile-share-toggle-${s.id}`}
                  className="h-9 px-3 border border-[#27272A] text-xs text-zinc-400 hover:text-white hover:bg-[#171717]"
                  title={s.role === "editor" ? "Byt till Läsare" : "Byt till Skribent"}
                >
                  {s.role === "editor" ? <Eye size={14} /> : <Pencil size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => removeShare(s.id)}
                  data-testid={`profile-share-delete-${s.id}`}
                  className="h-9 px-3 border border-[#27272A] text-[#FF3B30] hover:bg-[#FF3B30]/10"
                  title="Ta bort åtkomst"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        {!loading && shares.length === 0 && (
          <div className="pt-5 mt-2 border-t border-[#27272A] text-zinc-500 text-xs">
            Du har inte delat profilen med någon än.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RoleRow({ value, selected, testid, icon, title, subtitle }) {
  return (
    <label
      className={`flex items-start gap-3 border p-3 cursor-pointer transition-colors ${
        selected
          ? "border-[#34C759] bg-[#0f1a12]"
          : "border-[#27272A] hover:border-[#3F3F46]"
      }`}
      data-testid={testid}
    >
      <RadioGroupItem value={value} className="mt-0.5" />
      <div className="flex-1">
        <div className="flex items-center gap-2 font-display uppercase tracking-widest text-sm text-white">
          {icon}
          {title}
        </div>
        <div className="text-[11px] text-zinc-500 mt-1">{subtitle}</div>
      </div>
    </label>
  );
}
